import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertCircle, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Exam, Question, StudentAnswer } from '@/types/exam';
import { toast } from 'sonner';

interface ExamInterfaceProps {
  exam: Exam;
  onSubmit: (answers: StudentAnswer[]) => void;
  onCancel: () => void;
}

export default function ExamInterface({ exam, onSubmit, onCancel }: ExamInterfaceProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeRemaining, setTimeRemaining] = useState(exam.duration * 60); // in seconds
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock questions - replace with real data
  const questions: Question[] = [
    {
      id: '1',
      examId: exam.id,
      text: 'What is the capital of France?',
      type: 'multiple-choice',
      options: ['London', 'Berlin', 'Paris', 'Madrid'],
      correctAnswer: 'Paris',
      marks: 5,
      order: 1,
    },
    {
      id: '2',
      examId: exam.id,
      text: 'What is 2 + 2?',
      type: 'multiple-choice',
      options: ['3', '4', '5', '6'],
      correctAnswer: '4',
      marks: 5,
      order: 2,
    },
    {
      id: '3',
      examId: exam.id,
      text: 'Describe the water cycle in your own words.',
      type: 'essay',
      marks: 10,
      order: 3,
    },
  ];

  const currentQuestion = questions[currentQuestionIndex];

  // Timer effect
  useEffect(() => {
    if (timeRemaining <= 0) {
      handleSubmit();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 60 && prev % 10 === 0) {
          if (prev === 60) toast.warning('1 minute remaining!');
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Auto-save answers
  useEffect(() => {
    const autoSave = setInterval(() => {
      // Save to localStorage or backend
      localStorage.setItem(
        `exam_${exam.id}_answers`,
        JSON.stringify(answers)
      );
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSave);
  }, [answers, exam.id]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (answer: string) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: answer,
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const studentAnswers = questions.map((q) => ({
        questionId: q.id,
        answer: answers[q.id] || '',
      }));

      onSubmit(studentAnswers);
      toast.success('Exam submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit exam. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAnswered = answers[currentQuestion.id] !== undefined;
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;

  const timeWarning = timeRemaining < 300; // Less than 5 minutes

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className={`border-b ${timeWarning ? 'bg-red-50 border-red-200' : 'border-border'} sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary">{exam.name}</h1>
              <p className="text-sm text-muted-foreground">{exam.subject}</p>
            </div>

            <div className={`flex items-center gap-4 px-6 py-3 rounded-lg ${
              timeWarning ? 'bg-red-100 border border-red-300' : 'bg-primary/5 border border-border'
            }`}>
              <div className="flex items-center gap-2">
                <Clock className={`w-5 h-5 ${timeWarning ? 'text-red-600 animate-pulse' : 'text-primary'}`} />
                <span className={`text-lg font-mono font-bold ${timeWarning ? 'text-red-600' : 'text-primary'}`}>
                  {formatTime(timeRemaining)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <motion.div
            key={currentQuestion.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Question Header */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-primary">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </h2>
                <Badge variant="outline">{currentQuestion.marks} marks</Badge>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Question Card */}
            <Card className="p-6 border-border">
              <p className="text-lg font-medium text-foreground mb-6">
                {currentQuestion.text}
              </p>

              {/* Multiple Choice */}
              {currentQuestion.type === 'multiple-choice' && (
                <RadioGroup value={answers[currentQuestion.id] || ''} onValueChange={handleAnswerChange}>
                  <div className="space-y-3">
                    {currentQuestion.options?.map((option, index) => (
                      <div key={index} className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-primary/5 hover:border-primary cursor-pointer transition-all">
                        <RadioGroupItem value={option} id={`option-${index}`} />
                        <Label
                          htmlFor={`option-${index}`}
                          className="flex-1 cursor-pointer font-medium"
                        >
                          {option}
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              )}

              {/* Essay/Short Answer */}
              {(currentQuestion.type === 'essay' || currentQuestion.type === 'short-answer') && (
                <Textarea
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                  placeholder="Type your answer here..."
                  className="min-h-40 resize-none border-border"
                />
              )}
            </Card>

            {/* Answer Status */}
            {isAnswered && (
              <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <p className="text-green-700">Answer saved</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="border-t border-border bg-white sticky bottom-0">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <Button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              variant="outline"
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="w-4 h-4" />
              <span>{answeredCount} of {questions.length} answered</span>
            </div>

            {currentQuestionIndex === questions.length - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700 gap-2"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Exam'}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className="bg-primary hover:bg-primary/90 gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Confirm Cancel Dialog */}
      <AnimatePresence>
        {/* Can add confirm dialog here if needed */}
      </AnimatePresence>
    </div>
  );
}
